import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDate, fmtDateTime } from '../shared/format.ts';
import { useRouter } from '../shared/router.tsx';
import { ErrorBox, Spinner, useLoad } from '../shared/ui.tsx';

/**
 * Printing (ID-004): /papers/print?kind=labels|covers&ids=…
 * Labels are sized for DYMO LabelWriter 30256 (2-5/16" × 4"), one label per page:
 * paper number, defendant last name, barcode. Cover sheets print one per page for the deputy.
 */
export function PrintBatch() {
  const { me } = useAuth();
  const { path } = useRouter();
  const qs = new URLSearchParams(path.split('?')[1] ?? '');
  const kind = qs.get('kind') === 'covers' ? 'covers' : 'labels';
  const ids = qs.get('ids') ?? '';
  const d = useLoad(() => api.get<{ papers: any[] }>(`/api/papers/print-data?kind=${kind}&ids=${ids}`), [ids, kind]);
  if (d.error) return <ErrorBox error={d.error} />;
  if (!d.data) return <Spinner />;
  const papers = d.data.papers;
  const n = papers.length;
  return (
    <div className={`print-page print-${kind}`}>
      {kind === 'labels' && <style>{'@page { size: 4in 2.3125in; margin: 0; }'}</style>}
      {kind === 'covers' && <style>{'@page { size: letter; margin: 0.5in; }'}</style>}
      <div className="no-print row gap wrap">
        <button className="btn sm" onClick={() => history.back()}>
          ← Back
        </button>
        <button className="btn sm primary" onClick={() => window.print()}>
          Print {n} {kind === 'labels' ? 'label' : 'cover sheet'}
          {n === 1 ? '' : 's'}
        </button>
        {kind === 'labels' && <span className="small muted">DYMO 30256 (2-5/16″ × 4″). In the print dialog choose your LabelWriter, paper size 30256 Shipping, margins None, scale 100%.</span>}
      </div>
      {kind === 'labels'
        ? papers.map((p) => (
            <div className="dymo-label" key={p.id}>
              <div className={`dymo-num mono ${p.civilosNumber.length > 12 ? 'long' : p.civilosNumber.length > 9 ? 'mid' : ''}`}>{p.civilosNumber}</div>
              <div className="dymo-name">{p.lastName}</div>
              <img className="dymo-bar" src={`/api/papers/${p.id}/barcode.svg?text=0`} alt={`Barcode ${p.civilosNumber}`} />
            </div>
          ))
        : papers.map((p) => (
            <article className="cover-sheet" key={p.id}>
              <header className="cs-head">
                <div>
                  <div className="cs-agency">{me!.agency.name}</div>
                  <div className="cs-title">Service Cover Sheet</div>
                </div>
                <div className="cs-bar">
                  <img src={`/api/papers/${p.id}/barcode.svg`} alt={`Barcode ${p.civilosNumber}`} />
                </div>
              </header>
              <div className="cs-who">
                <div className="cs-name">{p.lastName}</div>
                <div className="cs-addr">{p.address}</div>
              </div>
              {p.bolos?.length > 0 && (
                <div className="cs-bolo">
                  <div className="cs-k">★ BOLO — SERVE URGENT</div>
                  {p.bolos.map((b: any, i: number) => (
                    <div key={i}>
                      Requested by <strong>{b.requestedBy ?? 'office'}</strong>
                      {b.note ? ` — ${b.note}` : ''}
                      {b.phone ? ` · Phone ${b.phone}` : ''}
                    </div>
                  ))}
                </div>
              )}
              {p.addressFlags?.length > 0 && (
                <div className="cs-alerts">
                  <div className="cs-k">⚠ ADDRESS HISTORY</div>
                  {p.addressFlags.map((t: string, i: number) => (
                    <div key={i}>{t}</div>
                  ))}
                </div>
              )}
              {p.alerts.length > 0 && (
                <div className="cs-alerts">
                  <div className="cs-k">⚠ SAFETY ALERTS</div>
                  {p.alerts.map((a: any, i: number) => (
                    <div key={i}>
                      <strong>{a.category}</strong>
                      {a.details ? ` — ${a.details}` : ''} <span className="cs-muted">({a.target === 'person' ? 'person' : 'address'})</span>
                    </div>
                  ))}
                </div>
              )}
              <table className="cs-table">
                <tbody>
                  <tr><th>Paper type</th><td>{p.paperType}{p.ivD ? ' · IV-D' : ''}</td></tr>
                  <tr><th>Case #</th><td className="mono">{p.caseNumber}</td></tr>
                  <tr><th>Court / date</th><td>{p.court ?? '—'} · {fmtDate(p.courtDate)}</td></tr>
                  <tr><th>Best time to serve</th><td><strong>{p.bestTime}</strong></td></tr>
                  <tr><th>Vehicle</th><td>{p.vehicle ?? '—'}</td></tr>
                  {p.vehiclesSeen.length > 0 && (
                    <tr>
                      <th>Seen at past attempts</th>
                      <td className="cs-veh">
                        {p.vehiclesSeen.map((v: any, i: number) => (
                          <div key={i}>
                            {v.text} <span className="cs-muted">({v.times}×, last {fmtDate(v.lastSeen)})</span>
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                  {p.phone && <tr><th>Phone</th><td>{p.phone}</td></tr>}
                  <tr><th>Employer</th><td>{p.employer ? `${p.employer}${p.employerAddress ? ` — ${p.employerAddress}` : ''}` : '—'}</td></tr>
                  {p.instructions && <tr><th>Instructions</th><td>{p.instructions}</td></tr>}
                </tbody>
              </table>
              <div className="cs-notes">
                <div className="cs-k">NOTES FROM PAST ATTEMPTS</div>
                {p.notes.length === 0 ? (
                  <div className="cs-muted">None yet.</div>
                ) : (
                  p.notes.map((x: any, i: number) => (
                    <div key={i}>
                      <span className="cs-muted">{fmtDateTime(x.at)} · {x.outcome}:</span> {x.notes}
                    </div>
                  ))
                )}
              </div>
              <div className="cs-attempts">
                <div className="cs-k">ATTEMPTS</div>
                <table>
                  <thead>
                    <tr><th>Date</th><th>Time</th><th>Result</th><th>Deputy</th></tr>
                  </thead>
                  <tbody>
                    {[1, 2, 3].map((i) => (
                      <tr key={i}><td></td><td></td><td></td><td></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <footer className="cs-foot mono">{p.civilosNumber}</footer>
            </article>
          ))}
    </div>
  );
}
